import React, { useEffect, useRef } from 'react';
import { ArrowDown, Code2, Database, Server, Sparkles } from 'lucide-react';
import { profileData } from '../data/mock';
import { Button } from './ui/button';

const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrollY = window.scrollY;
        const rotation = scrollY * 0.15;
        const graphic = heroRef.current.querySelector('.hero-graphic');
        if (graphic) {
          graphic.style.transform = `rotate(${rotation}deg)`;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0b]"
    >
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary glow */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-600/20 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[150px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {/* Hero graphic - rotating circles */}
        <div className="hero-graphic absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-[0.08] transition-transform duration-100">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <circle cx="100" cy="100" r="90" fill="none" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <circle cx="100" cy="100" r="70" fill="none" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <circle cx="100" cy="100" r="50" fill="none" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <circle cx="100" cy="100" r="30" fill="none" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <line x1="10" y1="100" x2="190" y2="100" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <line x1="100" y1="10" x2="100" y2="190" stroke="url(#heroGradient)" strokeWidth="0.3" />
            <line x1="30" y1="30" x2="170" y2="170" stroke="url(#heroGradient)" strokeWidth="0.2" />
            <line x1="170" y1="30" x2="30" y2="170" stroke="url(#heroGradient)" strokeWidth="0.2" />
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        {/* Floating Icons */}
        <div className="absolute top-1/4 left-1/4 animate-float-slow text-indigo-500/20">
          <Code2 size={48} />
        </div>
        <div className="absolute top-1/3 right-1/4 animate-float-medium text-purple-500/20">
          <Database size={40} />
        </div>
        <div className="absolute bottom-1/3 left-1/3 animate-float-fast text-indigo-500/20">
          <Server size={36} />
        </div>
        <div className="absolute bottom-1/4 right-1/3 animate-float-slow text-purple-500/20" style={{ animationDelay: '1s' }}>
          <Sparkles size={32} />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Greeting */}
        <div className="overflow-hidden mb-6">
          <p
            className="text-gray-500 text-lg animate-slide-up"
            style={{ fontFamily: 'Figtree, sans-serif', animationDelay: '0.2s' }}
          >
            Hello, I'm
          </p>
        </div>

        {/* Name */}
        <div className="overflow-hidden mb-4">
          <h1
            className="text-6xl md:text-8xl lg:text-9xl font-light text-white tracking-tight animate-slide-up"
            style={{ fontFamily: 'Figtree, sans-serif', animationDelay: '0.4s' }}
          >
            {profileData.name}
          </h1>
        </div>

        {/* Title with gradient */}
        <div className="overflow-hidden mb-8">
          <h2
            className="text-2xl md:text-3xl font-light animate-slide-up bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-text-gradient"
            style={{ fontFamily: 'Figtree, sans-serif', animationDelay: '0.6s', backgroundSize: '200% auto' }}
          >
            {profileData.title}
          </h2>
        </div>

        {/* Tagline */}
        <div className="overflow-hidden mb-12">
          <p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto animate-slide-up"
            style={{ fontFamily: 'Figtree, sans-serif', animationDelay: '0.8s' }}
          >
            {profileData.tagline}
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in"
          style={{ animationDelay: '1s' }}
        >
          <Button
            onClick={() => document.querySelector('#projects').scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-black px-8 py-6 text-lg hover:bg-gray-100 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
          >
            View My Work
          </Button>
          <Button
            variant="outline"
            onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-6 text-lg border-gray-700 text-gray-300 hover:bg-white/5 hover:border-gray-500 transition-all duration-500 hover:scale-105"
          >
            Get In Touch
          </Button>
        </div>

        {/* Tech Stack Pills */}
        <div
          className="flex flex-wrap justify-center gap-3 mt-16 animate-fade-in"
          style={{ animationDelay: '1.2s' }}
        >
          {['MongoDB', 'Express', 'React', 'Node.js'].map((tech, index) => (
            <span
              key={tech}
              className="px-4 py-2 text-sm text-gray-400 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-indigo-500/50 hover:text-white transition-all duration-500 cursor-default"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 text-gray-600 hover:text-white transition-all duration-500 animate-bounce"
      >
        <ArrowDown size={28} />
      </button>
    </section>
  );
};

export default Hero;
